/*
  MetaIntegrationSettings — Owner/Manager settings card for
  Facebook Page + Instagram Business Direct Message integration.

  Renders:
    - a config snapshot (which .env vars are set, plus the exact
      webhook URL + redirect URI the operator needs to paste into
      Meta's Developer Console),
    - the list of currently connected Pages (with linked IG
      account, token expiry, status, last error),
    - Connect / Reconnect / Disconnect actions,
    - a compact 3-step setup wizard for first-time users.

  Phase 1: read + connect only. Webhook + inbox UI ship in M2/M3.
*/
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MetaLeadForms } from "@/components/MetaLeadForms";
import {
  Facebook, Instagram, Link2, Loader2, RefreshCcw, Trash2, CheckCircle2,
  AlertTriangle, Copy, ExternalLink, ShieldCheck, Info, PlayCircle, XCircle,
  Eye, EyeOff, KeyRound, Save,
} from "lucide-react";

const OAUTH_REASONS = {
  invalid_state: "This connection link has expired or was already used (links last 10 minutes). Click Connect again.",
  permission_denied: "Facebook permissions weren't granted. Click Connect again and accept every requested permission, including lead access.",
  meta_error: "Facebook returned an error during sign-in. Click Connect again — open Diagnose below if it repeats.",
  missing_code: "Facebook didn't send a sign-in code back. Click Connect again.",
  token_exchange_failed: "Facebook didn't accept the sign-in code (it may have expired or the App secret / redirect URL is wrong). Click Connect again; open Diagnose below if it repeats.",
  config: "The Meta app settings are incomplete — check the App ID, App secret and redirect URL above.",
  page_discovery_failed: "Signed in, but your Facebook Pages couldn't be listed. Open Diagnose below for details.",
};

export const oauthReason = (reason, code) => {
  if (!reason) return "Try Connect again.";
  if (reason.startsWith("no_pages_")) return `No Facebook Page came back from Meta (${reason.slice(9).replace(/_/g, " ")}). Make sure you pick the Page in Meta's dialog.`;
  return (OAUTH_REASONS[reason] || reason.replace(/_/g, " ")) + (code ? ` (Facebook code ${code})` : "");
};

function CopyLine({ value, testid }) {
  return (
    <div className="flex items-center gap-1.5">
      <code
        className="flex-1 min-w-0 px-2 py-1 bg-zinc-100 rounded text-2xs font-mono text-zinc-800 break-all"
        data-testid={testid}
      >
        {value || <span className="text-zinc-400">(not set — configure META_* env vars)</span>}
      </code>
      {value && (
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2"
          onClick={() => {
            navigator.clipboard.writeText(value);
            toast.success("Copied");
          }}
          data-testid={`${testid}-copy`}
        >
          <Copy className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

function ConfigChip({ ok, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-2xs font-medium ${
        ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
           : "bg-amber-50 text-amber-800 border border-amber-200"
      }`}
      data-testid={`meta-config-chip-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
      {label}
    </span>
  );
}

function PasteSecretsPanel({ summary, onSaved }) {
  const [open, setOpen] = useState(!summary || !summary.active?.app_secret);
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [verifyToken, setVerifyToken] = useState("");
  const [loginConfigId, setLoginConfigId] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [saving, setSaving] = useState(false);

  const stored = summary?.stored || {};
  const active = summary?.active || {};
  const source = summary?.source || {};

  function sourceBadge(field) {
    const src = source[field];
    if (src === "dotenv") {
      return <Badge className="bg-zinc-100 text-zinc-700 border border-zinc-200 text-2xs">.env</Badge>;
    }
    if (src === "database") {
      return <Badge className="bg-indigo-100 text-indigo-800 border border-indigo-200 text-2xs">Database</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-2xs">Missing</Badge>;
  }

  async function save() {
    if (!appId.trim() && !appSecret.trim() && !verifyToken.trim() && !loginConfigId.trim()) {
      toast.warning("Nothing to save", { description: "Fill at least one field." });
      return;
    }
    setSaving(true);
    try {
      // Send only fields the operator actually typed. Empty string
      // means "leave as-is" on the backend, but we skip them here so
      // the payload is smaller and the intent is clearer.
      const payload = {};
      if (appId.trim())        payload.app_id = appId.trim();
      if (appSecret.trim())    payload.app_secret = appSecret.trim();
      if (verifyToken.trim())  payload.verify_token = verifyToken.trim();
      if (loginConfigId.trim()) payload.login_config_id = loginConfigId.trim();
      await api.put("/meta/secrets", payload);
      toast.success("Meta secrets saved", {
        description: "Encrypted to database and activated live — no restart needed.",
      });
      setAppId(""); setAppSecret(""); setVerifyToken(""); setLoginConfigId("");
      setShowSecret(false); setShowVerify(false);
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  async function clearAll() {
    if (!window.confirm("Clear all Meta secrets from the database? Any connected pages will need to be reconnected once new secrets are provided.")) return;
    setSaving(true);
    try {
      await api.delete("/meta/secrets");
      toast.success("Meta secrets cleared");
      onSaved?.();
    } catch (e) {
      toast.error("Clear failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  const anyStored = stored.app_id || stored.app_secret || stored.verify_token || stored.login_config_id;

  return (
    <details
      className="text-xs text-zinc-700 bg-white border border-indigo-200 rounded-md p-3"
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      data-testid="meta-paste-secrets-panel"
    >
      <summary className="cursor-pointer font-medium flex items-center gap-1.5 text-indigo-900">
        <KeyRound className="h-3.5 w-3.5" /> Paste Meta secrets (Owner only)
      </summary>
      <div className="mt-3 space-y-3">
        <div className="text-zinc-600">
          Skips the SSH-into-server-and-edit-<code>.env</code> step. Values are Fernet-encrypted before hitting Mongo, and activated live so <b>no backend restart is required</b>. If a value is also set in <code>backend/.env</code>, that wins over the database.
        </div>

        {/* Current-state grid */}
        <div className="grid grid-cols-3 gap-2 text-2xs">
          <div className="border border-zinc-200 rounded p-2 bg-zinc-50">
            <div className="text-zinc-500 uppercase tracking-wider text-2xs mb-1">App ID</div>
            <div className="flex items-center gap-1.5">
              {active.app_id ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" />
                              : <AlertTriangle className="h-3.5 w-3.5 text-amber-700" />}
              {sourceBadge("app_id")}
            </div>
          </div>
          <div className="border border-zinc-200 rounded p-2 bg-zinc-50">
            <div className="text-zinc-500 uppercase tracking-wider text-2xs mb-1">App Secret</div>
            <div className="flex items-center gap-1.5">
              {active.app_secret ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" />
                                  : <AlertTriangle className="h-3.5 w-3.5 text-amber-700" />}
              {sourceBadge("app_secret")}
            </div>
          </div>
          <div className="border border-zinc-200 rounded p-2 bg-zinc-50">
            <div className="text-zinc-500 uppercase tracking-wider text-2xs mb-1">Verify Token</div>
            <div className="flex items-center gap-1.5">
              {active.verify_token ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" />
                                    : <AlertTriangle className="h-3.5 w-3.5 text-amber-700" />}
              {sourceBadge("verify_token")}
            </div>
          </div>
          <div className="border border-zinc-200 rounded p-2 bg-zinc-50">
            <div className="text-zinc-500 uppercase tracking-wider text-2xs mb-1">
              Login config ID
            </div>
            <div className="flex items-center gap-1.5">
              {active.login_config_id ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" />
                                       : <AlertTriangle className="h-3.5 w-3.5 text-amber-700" />}
              {sourceBadge("login_config_id")}
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="space-y-2">
          <div>
            <Label className="text-2xs text-zinc-600" htmlFor="meta-secret-app-id">
              App ID <span className="text-zinc-400">· from Meta Dashboard → Settings → Basic</span>
            </Label>
            <Input
              id="meta-secret-app-id"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              placeholder={stored.app_id ? "•••••• (stored — enter to replace)" : "e.g. 123456789012345"}
              className="h-8 text-xs mt-1 font-mono"
              autoComplete="off"
              data-testid="meta-secret-app-id"
            />
          </div>
          <div>
            <Label className="text-2xs text-zinc-600" htmlFor="meta-secret-app-secret">
              App Secret <span className="text-zinc-400">· click Show in Meta Dashboard to reveal</span>
            </Label>
            <div className="relative mt-1">
              <Input
                id="meta-secret-app-secret"
                type={showSecret ? "text" : "password"}
                value={appSecret}
                onChange={(e) => setAppSecret(e.target.value)}
                placeholder={stored.app_secret ? "•••••• (stored — enter to replace)" : "32-char hex string"}
                className="h-8 text-xs pr-8 font-mono"
                autoComplete="off"
                data-testid="meta-secret-app-secret"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-800"
                onClick={() => setShowSecret((v) => !v)}
                aria-label={showSecret ? "Hide secret" : "Show secret"}
              >
                {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-2xs text-zinc-600" htmlFor="meta-secret-verify-token">
              Verify Token <span className="text-zinc-400">· the same string you paste into Meta&apos;s webhook wizard</span>
            </Label>
            <div className="relative mt-1">
              <Input
                id="meta-secret-verify-token"
                type={showVerify ? "text" : "password"}
                value={verifyToken}
                onChange={(e) => setVerifyToken(e.target.value)}
                placeholder={stored.verify_token ? "•••••• (stored — enter to replace)" : "random 32+ char string of your choosing"}
                className="h-8 text-xs pr-8 font-mono"
                autoComplete="off"
                data-testid="meta-secret-verify-token"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-800"
                onClick={() => setShowVerify((v) => !v)}
                aria-label={showVerify ? "Hide verify token" : "Show verify token"}
              >
                {showVerify ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </div>
            <button
              type="button"
              className="text-2xs text-indigo-700 hover:underline mt-1"
              onClick={() => {
                const buf = new Uint8Array(24);
                window.crypto.getRandomValues(buf);
                const b64 = btoa(String.fromCharCode(...buf))
                  .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
                setVerifyToken(b64);
                setShowVerify(true);
              }}
              data-testid="meta-verify-token-generate"
            >
              Generate a fresh random verify token
            </button>
          </div>
          <div>
            <Label className="text-2xs text-zinc-600" htmlFor="meta-secret-login-config-id">
              Login config ID <span className="text-zinc-400">· Facebook Login for Business preset (optional)</span>
            </Label>
            <Input
              id="meta-secret-login-config-id"
              value={loginConfigId}
              onChange={(e) => setLoginConfigId(e.target.value)}
              placeholder={stored.login_config_id
                ? "•••••• (stored — enter to replace)"
                : "e.g. 1689593162885086"}
              className="h-8 text-xs mt-1 font-mono"
              autoComplete="off"
              data-testid="meta-secret-login-config-id"
            />
            <div className="text-2xs text-zinc-500 mt-1">
              Grab this from <code className="font-mono">Meta Dashboard → Facebook Login for Business → Configurations</code>.
              When set, the authorize URL uses <code className="font-mono">config_id</code> (the preset's permissions
              bundle) and the ad-hoc <code className="font-mono">scope</code> parameter is omitted — Meta rejects dialogs
              that carry both.
            </div>
          </div>
        </div>

        {summary?.updated_at && (
          <div className="text-2xs text-zinc-500">
            Last updated {new Date(summary.updated_at).toLocaleString()}
            {summary.updated_by?.name && <> by <b>{summary.updated_by.name}</b></>}
          </div>
        )}

        <div className="flex gap-2 pt-1 items-center">
          <Button
            size="sm"
            className="h-8"
            onClick={save}
            disabled={saving}
            data-testid="meta-secrets-save-btn"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save secrets
          </Button>
          {anyStored && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-rose-700 border-rose-200 hover:bg-rose-50"
              onClick={clearAll}
              disabled={saving}
              data-testid="meta-secrets-clear-btn"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear stored
            </Button>
          )}
          <span className="text-2xs text-zinc-500 flex items-center gap-1 ml-auto">
            <ShieldCheck className="h-3 w-3" /> Encrypted at rest (Fernet)
          </span>
        </div>
      </div>
    </details>
  );
}


function ConnectionRow({ conn, onRefresh, onDisconnect, busy, isOwner }) {
  const status = conn.status || "connected";
  const healthy = conn.token_is_valid && status === "subscribed";
  const ig = conn.instagram_business_account;
  const leadFailed = conn.last_lead_status === "failed";
  return (
    <li
      className="p-3 border border-zinc-200 rounded-md bg-white space-y-2"
      data-testid={`meta-conn-row-${conn.page_id}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Facebook className="h-4 w-4 text-[#1877F2]" />
        <span className="font-medium text-sm text-zinc-900">
          {conn.page_name || conn.page_id}
        </span>
        {ig ? (
          <span className="inline-flex items-center gap-1 text-xs text-zinc-600">
            <Instagram className="h-3.5 w-3.5 text-pink-600" />
            @{ig.username || ig.id}
          </span>
        ) : (
          <span className="text-2xs text-zinc-400 italic">no linked Instagram</span>
        )}
        <div className="flex-1" />
        <Badge
          className={healthy
            ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
            : "bg-amber-100 text-amber-800 border border-amber-200"}
          data-testid={`meta-conn-badge-${conn.page_id}`}
        >
          {healthy ? "Healthy" : (status === "subscribe_failed" ? "Reconnect needed" : status)}
        </Badge>
      </div>
      <div className="text-2xs text-zinc-500 grid grid-cols-2 gap-x-3 gap-y-1">
        <div>Token ends in <code className="px-1 py-0.5 bg-zinc-100 rounded">…{conn.token_last6 || "??????"}</code></div>
        {conn.token_expires_at && (
          <div>Expires: <span className="text-zinc-700">{new Date(conn.token_expires_at).toLocaleString()}</span></div>
        )}
        <div>Scopes: <span className="text-zinc-700">{(conn.scopes || []).length}</span></div>
        <div>Connected: <span className="text-zinc-700">
          {conn.connected_at ? new Date(conn.connected_at).toLocaleDateString() : "—"}
        </span></div>
      </div>
      {conn.last_error && (
        <div className="text-2xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 break-words">
          {conn.last_error}
        </div>
      )}
      <div className={`text-2xs ${!conn.leadgen_subscribed ? "text-amber-700" : leadFailed ? "text-rose-700" : "text-emerald-700"}`} data-testid={`meta-conn-leads-${conn.page_id}`}>
        Lead forms: {conn.leadgen_subscribed ? "receiving" : `not connected${conn.has_leads_permission ? "" : " — lead access (leads_retrieval) not granted"}`}
        {conn.last_lead_at ? ` · last lead ${new Date(conn.last_lead_at).toLocaleString()} (${leadFailed ? "couldn't be added" : conn.last_lead_status || "?"})` : ""}
        {leadFailed && conn.last_lead_error ? <span className="block break-words" data-testid={`meta-conn-lead-error-${conn.page_id}`}>{conn.last_lead_error}</span> : null}
        {!conn.leadgen_subscribed && conn.leadgen_error ? <span className="block text-zinc-500 break-words">{conn.leadgen_error}</span> : null}
      </div>
      {isOwner && <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={() => onRefresh(conn.page_id)}
          disabled={busy === conn.page_id}
          data-testid={`meta-conn-refresh-${conn.page_id}`}
        >
          {busy === conn.page_id
            ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            : <RefreshCcw className="h-3 w-3 mr-1" />}
          Refresh
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs text-rose-700 border-rose-200 hover:bg-rose-50"
          onClick={() => onDisconnect(conn.page_id, conn.page_name)}
          disabled={busy === conn.page_id}
          data-testid={`meta-conn-disconnect-${conn.page_id}`}
        >
          <Trash2 className="h-3 w-3 mr-1" /> Disconnect
        </Button>
      </div>}
    </li>
  );
}

export default function MetaIntegrationSettings() {
  const { can } = useAuth();
  const isOwner = can("owner");
  const [cfg, setCfg] = useState(null);
  const [secrets, setSecrets] = useState(null);
  const [conns, setConns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [diagnosis, setDiagnosis] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadDiagnosis = useCallback(async () => {
    setDiagLoading(true);
    try {
      const r = await api.get("/meta/oauth/diagnose");
      setDiagnosis(r.data);
    } catch (e) {
      toast.error("Couldn't load Meta OAuth diagnosis", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setDiagLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        api.get("/meta/config"),
        api.get("/meta/connections"),
      ];
      if (isOwner) requests.push(api.get("/meta/secrets"));
      const [cfgR, connR, secretsR] = await Promise.all(requests);
      setCfg(cfgR.data);
      setConns(connR.data.connections || []);
      if (secretsR) setSecrets(secretsR.data);
    } catch (e) {
      toast.error("Couldn't load Meta integration state", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, [isOwner]);

  useEffect(() => { load(); }, [load]);

  // Meta redirects back to /settings?tab=contacts&meta=success|partial|error&…
  useEffect(() => {
    const outcome = searchParams.get("meta");
    if (!outcome) return;
    const leadsMissing = searchParams.get("leads") === "missing";
    const leadNote = leadsMissing ? "Lead forms aren't fully connected — lead access (leads_retrieval) or the leadgen subscription is missing. Use Check lead access below." : "Messenger, Instagram and lead forms are connected.";
    if (outcome === "success") {
      toast[leadsMissing ? "warning" : "success"](`Connected ${searchParams.get("pages") || ""} Page(s) to Meta`, { id: "meta-oauth-outcome", description: leadNote });
    } else if (outcome === "partial") {
      toast.warning("Connected — but subscription had errors", {
        id: "meta-oauth-outcome",
        description: searchParams.get("err") || "Check the Page row for details.",
      });
    } else if (outcome === "error") {
      toast.error("Meta connection failed", { id: "meta-oauth-outcome", description: oauthReason(searchParams.get("reason"), searchParams.get("code")), duration: 12000 });
      // Automatically surface the sanitised diagnosis so the Owner
      // doesn't have to go hunting for it.
      loadDiagnosis();
    }
    const next = new URLSearchParams(searchParams);
    ["meta", "pages", "reason", "err", "leads", "code"].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
    load();
  }, [searchParams, setSearchParams, load, loadDiagnosis]);

  async function connect() {
    const missing = cfg && Object.entries(cfg.configured).filter(([, v]) => !v).map(([k]) => k);
    if (missing && missing.length) {
      toast.error("Meta env vars missing", {
        description: `Set these in backend/.env first: ${missing.join(", ")}`,
      });
      return;
    }
    setBusy("connect");
    try {
      const r = await api.post("/meta/oauth/start");
      window.location.assign(r.data.authorize_url);
    } catch (e) {
      setBusy(null);
      toast.error("Couldn't start the Meta connection", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function refresh(pageId) {
    setBusy(pageId);
    try {
      const r = await api.post(`/meta/connections/${pageId}/refresh`);
      setConns((cur) => cur.map((c) => c.page_id === pageId ? r.data : c));
      toast.success("Connection refreshed");
    } catch (e) {
      toast.error("Refresh failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(null); }
  }

  async function disconnect(pageId, pageName) {
    if (!window.confirm(`Disconnect ${pageName || pageId}? Meta will stop forwarding messages until you reconnect.`)) return;
    setBusy(pageId);
    try {
      await api.delete(`/meta/connections/${pageId}`);
      setConns((cur) => cur.filter((c) => c.page_id !== pageId));
      toast.success("Disconnected");
    } catch (e) {
      toast.error("Disconnect failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(null); }
  }

  async function runTestWebhook() {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/meta/test-webhook");
      setTestResult(r.data);
      if (r.data.ok) {
        toast.success("Webhook self-test passed", {
          description: "All 5 checks green — HMAC, verify token, dispatch, idempotency, bad-sig reject.",
        });
      } else {
        toast.warning("Webhook self-test found issues", {
          description: "Expand the report below to see which step failed.",
        });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setTestResult({ ok: false, error: detail, steps: [] });
      toast.error("Self-test couldn't run", { description: detail });
    } finally {
      setTesting(false);
    }
  }

  // login_config_id is optional (Facebook Login for Business preset).
  // Connect only requires the 3 secrets + server-side bits (encryption key,
  // redirect URI, graph version) which are always configured.
  const allConfigured = cfg && Object.entries(cfg.configured)
    .filter(([k]) => k !== "login_config_id")
    .every(([, v]) => Boolean(v));

  return (
    <Card className="p-4 rounded-lg border border-zinc-200 shadow-none" data-testid="meta-settings-card">
      <div className="mb-3">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
          <Facebook className="h-3.5 w-3.5" /> / <Instagram className="h-3.5 w-3.5" />
          Facebook &amp; Instagram Direct Messages
        </div>
        <div className="text-sm text-zinc-600">
          Capture Facebook Page Messenger + Instagram Business DMs directly into your Enquiries inbox. Phase 1 is read-only; replies happen in Meta Business Suite until Phase 2 ships.
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-zinc-500 py-4">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading Meta integration…
        </div>
      ) : (
        <div className="space-y-4">
          {/* Config chips */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="meta-config-chips">
            <ConfigChip ok={!!cfg?.configured?.app_id}         label="App ID" />
            <ConfigChip ok={!!cfg?.configured?.app_secret}      label="App Secret" />
            <ConfigChip ok={!!cfg?.configured?.verify_token}    label="Verify Token" />
            <ConfigChip ok={!!cfg?.configured?.login_config_id} label="Login config ID" />
            <ConfigChip ok={!!cfg?.configured?.encryption_key}  label="Encryption Key" />
            <ConfigChip ok={!!cfg?.configured?.redirect_uri}    label="Redirect URI" />
            <ConfigChip ok={!!cfg?.configured?.graph_version}   label={`Graph ${cfg?.graph_version || "?"}`} />
          </div>

          {!allConfigured && (
            <div className="border border-amber-200 bg-amber-50 rounded-md p-3 text-xs text-amber-900 space-y-2" data-testid="meta-setup-warning">
              <div className="flex items-start gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <div>
                  Some Meta secrets aren&apos;t set yet. The <b>Connect</b> button won&apos;t work until they are.
                  {isOwner
                    ? <> Use the <b>Paste Meta secrets</b> panel below (no server access needed).</>
                    : <> See the 3-step setup below — an Owner will need to fill three env vars.</>}
                </div>
              </div>
            </div>
          )}

          {/* Owner-only paste-secrets panel — no SSH required */}
          {isOwner && (
            <PasteSecretsPanel summary={secrets} onSaved={load} />
          )}

          {/* Setup wizard (collapsed by default when connected) */}
          <details className="text-xs text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-md p-3" open={!allConfigured || conns.length === 0}>
            <summary className="cursor-pointer font-medium flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" /> One-time Meta Developer Console setup
            </summary>
            <ol className="list-decimal list-inside space-y-2 mt-2 pl-1 text-zinc-700">
              <li>
                Open <a className="text-indigo-700 underline" href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer">developers.facebook.com/apps</a> → <b>Create App</b>. Choose <b>Business</b> type, name it &quot;LE Hub Enquiries&quot;, link to your Business account.
              </li>
              <li>
                Add these products to the app: <b>Facebook Login for Business</b>, <b>Messenger</b>, <b>Webhooks</b>. In Facebook Login → <b>Settings → Valid OAuth Redirect URIs</b>, paste:
                <div className="mt-1"><CopyLine value={cfg?.redirect_uri} testid="meta-redirect-uri" /></div>
              </li>
              <li>
                In <b>Messenger → API Setup → Webhooks → Configure webhook</b> (and in Instagram Messaging when it appears), paste this callback URL:
                <div className="mt-1"><CopyLine value={cfg?.webhook_url} testid="meta-webhook-url" /></div>
                For the &quot;Verify token&quot; field, invent any random string (e.g. UUID from a password manager). Paste the same string into <code>META_VERIFY_TOKEN</code> in <code>backend/.env</code>. Meta stores its own copy of this string and echoes it back to us on the first webhook probe — that's how we know the callback URL is really from Meta.
              </li>
              <li>
                In <b>App Dashboard → Settings → Basic</b> copy the <b>App ID</b> and <b>App Secret</b> (click Show + re-authenticate). Paste them into <code>backend/.env</code> as:
                <pre className="bg-zinc-100 p-2 rounded text-2xs font-mono mt-1 whitespace-pre-wrap">META_APP_ID=…
META_APP_SECRET=…
META_VERIFY_TOKEN=…</pre>
                Then run <code>sudo supervisorctl restart backend</code> and refresh this page.
              </li>
              <li>
                Add yourself as an app <b>Admin</b> under <b>App Roles → Roles</b>, and add your Facebook Page + Instagram Professional account as a <b>Test asset</b>. Until App Review is granted, only Admins/Testers can send DMs that reach us.
              </li>
              <li>
                Once the chips above are all green, hit <b>Connect Facebook Page</b> below. You&apos;ll be sent to Meta&apos;s consent screen, then bounced back here with your Page listed.
              </li>
            </ol>
            <div className="mt-3 text-2xs text-zinc-500 flex items-start gap-1">
              <Info className="h-3 w-3 mt-0.5" />
              Full App Review with the six requested permissions is required before non-admin users can DM you. We&apos;ll help package the screencast when M3 (Social Inbox UI) ships.
            </div>
          </details>

          {/* Connect + Test buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={connect}
              disabled={!allConfigured || !isOwner || busy === "connect"}
              title={isOwner ? undefined : "Only the owner can connect or reconnect Facebook & Instagram"}
              data-testid="meta-connect-btn"
            >
              {busy === "connect" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
              {conns.length ? "Reconnect / connect another Page" : "Connect Facebook Page + Instagram"}
            </Button>
            {!isOwner && <span className="text-xs text-zinc-500" data-testid="meta-connect-owner-only">Only the owner can connect or reconnect.</span>}
            <Button
              variant="outline"
              onClick={runTestWebhook}
              disabled={testing || !cfg?.configured?.app_secret || !cfg?.configured?.verify_token}
              data-testid="meta-test-webhook-btn"
              title="Sends a signed synthetic Meta payload through the real webhook handler to prove HMAC verification + dedupe + verify-token challenge all work."
            >
              {testing
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <PlayCircle className="h-4 w-4 mr-2" />}
              Test webhook
            </Button>
            <Button
              variant="outline"
              onClick={loadDiagnosis}
              disabled={diagLoading}
              data-testid="meta-diagnose-btn"
              title="Show the sanitised diagnosis of the most recent OAuth attempt — permissions granted/declined, Pages found via /me/accounts, Business Portfolio lookups, etc. Never shows tokens."
            >
              {diagLoading
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <PlayCircle className="h-4 w-4 mr-2" />}
              Diagnose connection
            </Button>
            <Button
              variant="outline"
              onClick={load}
              data-testid="meta-reload-btn"
            >
              <RefreshCcw className="h-4 w-4 mr-2" /> Reload
            </Button>
            <a
              className="text-xs text-zinc-500 hover:text-zinc-700 inline-flex items-center gap-1"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/meta/setup-guide`}
              target="_blank" rel="noreferrer"
              data-testid="meta-setup-guide-link"
            >
              Setup guide <ExternalLink className="h-3 w-3" />
            </a>
            <a
              className="text-xs text-zinc-500 hover:text-zinc-700 inline-flex items-center gap-1"
              href="https://business.facebook.com/latest/inbox"
              target="_blank" rel="noreferrer"
            >
              Meta Business Inbox <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          {/* Self-test result */}
          {testResult && (
            <div
              className={`rounded-md border p-3 text-xs space-y-2 ${
                testResult.ok
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "border-rose-200 bg-rose-50 text-rose-900"
              }`}
              data-testid="meta-test-webhook-result"
            >
              <div className="flex items-center gap-1.5 font-medium">
                {testResult.ok
                  ? <><CheckCircle2 className="h-4 w-4" /> Self-test passed — the Meta webhook plumbing is live.</>
                  : <><AlertTriangle className="h-4 w-4" /> Self-test found issues — fix the failing step(s) below.</>}
              </div>
              {testResult.page_name && (
                <div className="text-2xs">
                  Attached to page: <b>{testResult.page_name}</b>{" "}
                  <span className="opacity-70">({testResult.page_id})</span>
                  {" · "}mid: <code className="px-1 py-0.5 bg-white/60 rounded">{testResult.mid}</code>
                </div>
              )}
              {testResult.error && (
                <div className="text-2xs break-words">{testResult.error}</div>
              )}
              {(testResult.steps || []).length > 0 && (
                <ul className="space-y-1">
                  {testResult.steps.map((s) => (
                    <li key={s.key} className="flex items-start gap-1.5" data-testid={`meta-test-step-${s.key}`}>
                      {s.ok
                        ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-emerald-700" />
                        : <XCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-rose-700" />}
                      <div className="flex-1">
                        <div>{s.label}</div>
                        {s.detail && <div className="text-2xs opacity-70">{s.detail}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* OAuth diagnosis panel — surfaces WHY the connection
              failed. Tokens / codes / app secret are already stripped
              server-side (meta_client.sanitise_diagnosis). */}
          {diagnosis && diagnosis.present && (
            <div
              className="rounded-md border p-3 text-xs space-y-2 border-amber-200 bg-amber-50 text-amber-950"
              data-testid="meta-diagnose-result"
            >
              <div className="flex items-center gap-1.5 font-medium">
                <AlertTriangle className="h-4 w-4" /> Last OAuth attempt — diagnosis
                <span className="ml-auto text-2xs opacity-70">
                  {diagnosis.updated_at}
                </span>
              </div>
              {diagnosis.stage && (
                <div className="text-2xs">
                  Stage reached: <b>{diagnosis.stage}</b>
                </div>
              )}
              {diagnosis.user && (
                <div className="text-2xs">
                  Facebook user:{" "}
                  {diagnosis.user.error
                    ? <code className="text-rose-800">{JSON.stringify(diagnosis.user.error)}</code>
                    : <><b>{diagnosis.user.name || "?"}</b>{" "}<span className="opacity-70">({diagnosis.user.id})</span></>}
                </div>
              )}
              {diagnosis.permissions && (
                <div className="text-2xs space-y-1">
                  <div><b>Granted:</b>{" "}
                    <span className="font-mono-num">
                      {(diagnosis.permissions.granted || []).join(", ") || "— none —"}
                    </span>
                  </div>
                  {(diagnosis.permissions.declined || []).length > 0 && (
                    <div><b>Declined:</b>{" "}
                      <span className="font-mono-num text-rose-800">
                        {diagnosis.permissions.declined.join(", ")}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {diagnosis.me_accounts && (
                <div className="text-2xs">
                  <b>/me/accounts</b> → {diagnosis.me_accounts.count ?? 0} page(s)
                  {diagnosis.me_accounts.error && (
                    <code className="ml-1 text-rose-800 break-words">
                      {JSON.stringify(diagnosis.me_accounts.error)}
                    </code>
                  )}
                </div>
              )}
              {diagnosis.me_businesses && (
                <div className="text-2xs">
                  <b>/me/businesses</b> → {diagnosis.me_businesses.count ?? 0} business(es)
                  {diagnosis.me_businesses.error && (
                    <code className="ml-1 text-rose-800 break-words">
                      {JSON.stringify(diagnosis.me_businesses.error)}
                    </code>
                  )}
                </div>
              )}
              {Array.isArray(diagnosis.business_owned_pages) && diagnosis.business_owned_pages.length > 0 && (
                <div className="text-2xs">
                  <b>Business-owned Pages:</b>{" "}
                  {diagnosis.business_owned_pages.map((b, i) => (
                    <span key={i} className="mr-2">
                      {b.business_id}:{b.count || 0}
                      {b.error && <code className="text-rose-800">({JSON.stringify(b.error).slice(0, 60)})</code>}
                    </span>
                  ))}
                </div>
              )}
              {diagnosis.target_page_direct && (
                <div className="text-2xs">
                  <b>Direct Page fetch:</b>{" "}
                  {diagnosis.target_page_direct.ok
                    ? <span className="text-emerald-800">resolved</span>
                    : <code className="text-rose-800">{JSON.stringify(diagnosis.target_page_direct.error)}</code>}
                </div>
              )}
              {typeof diagnosis.pages_total === "number" && (
                <div className="text-2xs pt-1 border-t border-amber-200">
                  <b>Pages manageable in total:</b> {diagnosis.pages_total}
                </div>
              )}
              {(diagnosis.warnings || []).length > 0 && (
                <ul className="list-disc list-inside text-2xs space-y-0.5">
                  {diagnosis.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
              {diagnosis.graph_error && (
                <div className="text-2xs">
                  <b>Graph error:</b>{" "}
                  <code className="text-rose-800 break-words">
                    {JSON.stringify(diagnosis.graph_error)}
                  </code>
                </div>
              )}
            </div>
          )}

          {/* Connections list */}
          <div>
            <div className="text-2xs uppercase tracking-wider text-zinc-500 font-semibold mb-1">
              Connected Pages ({conns.length})
            </div>
            {conns.length === 0 ? (
              <div className="text-xs italic text-zinc-400 border border-dashed border-zinc-200 rounded p-4 text-center" data-testid="meta-conn-empty">
                No Pages connected yet.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="meta-conn-list">
                {conns.map((c) => (
                  <ConnectionRow
                    key={c.page_id}
                    conn={c}
                    onRefresh={refresh}
                    onDisconnect={disconnect}
                    isOwner={isOwner}
                    busy={busy}
                  />
                ))}
              </ul>
            )}
            <div className="mt-3"><MetaLeadForms conns={conns} onChanged={() => api.get("/meta/connections").then((r) => setConns(r.data.connections || [])).catch(() => {})} /></div>
          </div>
        </div>
      )}
    </Card>
  );
}
